import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_VERSIONS = 5;
const DEBOUNCE_MS = 3000; // 3 seconds minimum between version snapshots

export interface CodeVersion {
  timestamp: number;
  html: string;
  css: string;
  js: string;
}

interface UseCodeVersionHistoryParams {
  storageKey: string;
  html: string;
  css: string;
  js: string;
}

/**
 * Manages version history for code editor content.
 * - Automatically saves snapshots (debounced, max 5 versions)
 * - Provides restore functionality
 * - Persists to localStorage
 */
export const useCodeVersionHistory = ({
  storageKey,
  html,
  css,
  js,
}: UseCodeVersionHistoryParams) => {
  const historyKey = `${storageKey}-history`;

  const [versions, setVersions] = useState<CodeVersion[]>(() => {
    try {
      const stored = localStorage.getItem(historyKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Initialize lastSaveRef with most recent version timestamp to prevent immediate save on mount
  const lastSaveRef = useRef<number>(versions[0]?.timestamp ?? 0);

  // Save a new version (debounced by DEBOUNCE_MS)
  const saveVersion = useCallback(() => {
    const now = Date.now();
    if (now - lastSaveRef.current < DEBOUNCE_MS) {
      return;
    }

    // Don't save if all code is empty
    if (!html && !css && !js) {
      return;
    }

    lastSaveRef.current = now;

    setVersions((prev) => {
      // Skip if code hasn't changed from most recent version
      const latest = prev[0];
      if (latest && latest.html === html && latest.css === css && latest.js === js) {
        return prev;
      }

      const newVersion: CodeVersion = { timestamp: now, html, css, js };
      const updated = [newVersion, ...prev].slice(0, MAX_VERSIONS);

      try {
        localStorage.setItem(historyKey, JSON.stringify(updated));
      } catch {
        // Quota exceeded - don't persist but keep in memory
      }

      return updated;
    });
  }, [html, css, js, historyKey]);

  // Auto-save on code changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(saveVersion, DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [html, css, js, saveVersion]);

  // Force immediate save on Cmd+Shift+S (bypass debounce)
  useEffect(() => {
    const handler = () => {
      lastSaveRef.current = 0; // Reset debounce so saveVersion runs immediately
      saveVersion();
    };
    window.addEventListener('bds:save-shortcut', handler);
    return () => window.removeEventListener('bds:save-shortcut', handler);
  }, [saveVersion]);

  // Restore a specific version
  const restoreVersion = useCallback(
    (
      index: number,
      setHtml: (v: string) => void,
      setCss: (v: string) => void,
      setJs: (v: string) => void
    ) => {
      const version = versions[index];
      if (version) {
        setHtml(version.html);
        setCss(version.css);
        setJs(version.js);
      }
    },
    [versions]
  );

  // Format timestamp as relative time
  const getRelativeTime = (timestamp: number): string => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400)
      return `${Math.floor(seconds / 3600)} hour${seconds >= 7200 ? 's' : ''} ago`;
    return `${Math.floor(seconds / 86400)} day${seconds >= 172800 ? 's' : ''} ago`;
  };

  return {
    versions,
    restoreVersion,
    getRelativeTime,
  };
};
